import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  CreditCard, 
  Search, 
  Shield, 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  Undo2,
  Eye,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Trash2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const API = "/api";

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-[#111] border border-[#222] rounded-xl p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[#666] text-xs mb-1">{title}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
    </div>
  </div>
);

export default function AdminPayments() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  const [viewTransaction, setViewTransaction] = useState(null);
  const [refundTransaction, setRefundTransaction] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [deleteTransaction, setDeleteTransaction] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const adminEmail = localStorage.getItem("admin_email");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return;
    }
    fetchTransactions();
    fetchSummary();
  }, [navigate, page, search, statusFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: "25" });
      if (search) params.append("search", search);
      if (statusFilter !== "all") params.append("status", statusFilter);

      const response = await fetch(`${API}/admin/payments/transactions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
        return;
      }

      const data = await response.json();
      setTransactions(data.transactions || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const response = await fetch(`${API}/admin/payments/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setSummary(data);
    } catch (error) {
      console.error("Failed to fetch summary");
    }
  };

  const handleRefund = async () => {
    if (!refundTransaction) return;
    setRefundLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/payments/refund/${refundTransaction.transaction_id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Refund failed");
      }

      toast.success("Refund processed successfully");
      setRefundTransaction(null);
      fetchTransactions();
      fetchSummary();
    } catch (error) {
      toast.error(error.message || "Refund failed");
    } finally {
      setRefundLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTransaction) return;
    setDeleteLoading(true);
    const token = localStorage.getItem("admin_token");
    
    try {
      const response = await fetch(`${API}/admin/payments/${deleteTransaction.transaction_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Delete failed");
      }

      toast.success("Transaction deleted successfully");
      setDeleteTransaction(null);
      fetchTransactions();
      fetchSummary();
    } catch (error) {
      toast.error(error.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_email");
    navigate("/login");
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: { class: "bg-green-500/20 text-green-400 border-green-500/30", icon: CheckCircle },
      pending: { class: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: Clock },
      failed: { class: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle },
      refunded: { class: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Undo2 },
    };
    return styles[status] || styles.pending;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD"
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white mb-1">Payment Management</h1>
          <p className="text-[#666]">{total} total transactions</p>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard title="Total Revenue" value={`$${summary.total_revenue}`} icon={DollarSign} color="bg-green-600" />
            <StatCard title="MRR" value={`$${summary.mrr}`} icon={TrendingUp} color="bg-blue-600" />
            <StatCard title="Paid" value={summary.paid_count} icon={CheckCircle} color="bg-emerald-600" />
            <StatCard title="Refunded" value={summary.refunded_count} icon={Undo2} color="bg-purple-600" />
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" />
            <Input
              placeholder="Search by email or transaction ID..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 bg-[#111] border-[#333] text-white placeholder:text-[#555]"
            />
          </div>
          
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px] bg-[#111] border-[#333] text-white">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-[#333]">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              <SelectItem value="paid" className="text-white">Paid</SelectItem>
              <SelectItem value="pending" className="text-white">Pending</SelectItem>
              <SelectItem value="failed" className="text-white">Failed</SelectItem>
              <SelectItem value="refunded" className="text-white">Refunded</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0a0a0a] border-b border-[#222]">
                <tr>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Transaction</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">User</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Plan</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Amount</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Status</th>
                  <th className="text-left text-[#888] text-sm font-medium px-6 py-4">Date</th>
                  <th className="text-right text-[#888] text-sm font-medium px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-[#555] mx-auto" />
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-[#555]">No transactions found</td>
                  </tr>
                ) : (
                  transactions.map((txn) => {
                    const statusInfo = getStatusBadge(txn.payment_status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <tr key={txn.transaction_id} className="border-b border-[#222] hover:bg-[#1a1a1a] transition-colors">
                        <td className="px-6 py-4">
                          <p className="text-white font-mono text-xs">{txn.transaction_id}</p>
                        </td>
                        <td className="px-6 py-4 text-[#888] text-sm">{txn.user_email}</td>
                        <td className="px-6 py-4">
                          <span className="text-white text-sm">
                            {txn.plan_id === "pro" ? "Pro Plan" : `Plus (${txn.conversations || 0})`}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-white font-medium">
                          {formatAmount(txn.amount, txn.currency)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full border ${statusInfo.class}`}>
                            <StatusIcon className="w-3 h-3" />
                            {txn.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[#888] text-sm">{formatDate(txn.created_at)}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewTransaction(txn)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {txn.payment_status === "paid" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setRefundTransaction(txn)}
                                className="text-purple-400 hover:text-purple-300 hover:bg-purple-400/10"
                              >
                                <Undo2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteTransaction(txn)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                              data-testid={`delete-payment-${txn.transaction_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-[#222]">
              <p className="text-[#666] text-sm">Page {page} of {totalPages}</p>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-[#888] hover:text-white disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="text-[#888] hover:text-white disabled:opacity-50">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* View Transaction Modal */}
      <Dialog open={!!viewTransaction} onOpenChange={() => setViewTransaction(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-400" />
              Transaction Details
            </DialogTitle>
          </DialogHeader>
          
          {viewTransaction && (
            <div className="space-y-4 py-4">
              <div>
                <p className="text-[#666] text-xs mb-1">Transaction ID</p>
                <p className="text-white font-mono text-sm">{viewTransaction.transaction_id}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#666] text-xs mb-1">Amount</p>
                  <p className="text-white text-lg font-bold">{formatAmount(viewTransaction.amount, viewTransaction.currency)}</p>
                </div>
                <div>
                  <p className="text-[#666] text-xs mb-1">Status</p>
                  <p className={`${viewTransaction.payment_status === "paid" ? "text-green-400" : viewTransaction.payment_status === "refunded" ? "text-purple-400" : "text-yellow-400"}`}>
                    {viewTransaction.payment_status}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-[#666] text-xs mb-1">User</p>
                <p className="text-white">{viewTransaction.user_email}</p>
              </div>
              <div>
                <p className="text-[#666] text-xs mb-1">Plan</p>
                <p className="text-white">{viewTransaction.plan_id === "pro" ? "Pro Plan ($29/mo)" : `Plus (${viewTransaction.conversations} conversations)`}</p>
              </div>
              <div>
                <p className="text-[#666] text-xs mb-1">Date</p>
                <p className="text-white">{formatDate(viewTransaction.created_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refund Confirmation Modal */}
      <Dialog open={!!refundTransaction} onOpenChange={() => setRefundTransaction(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-purple-500" />
              Issue Refund
            </DialogTitle>
            <DialogDescription className="text-[#888]">
              {refundTransaction && (
                <>Refund {formatAmount(refundTransaction.amount, refundTransaction.currency)} to {refundTransaction.user_email}?</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRefundTransaction(null)} className="text-[#888] hover:text-white">Cancel</Button>
            <Button onClick={handleRefund} disabled={refundLoading} className="bg-purple-600 hover:bg-purple-700">
              {refundLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Issue Refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteTransaction} onOpenChange={() => setDeleteTransaction(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Transaction
            </DialogTitle>
            <DialogDescription className="text-[#888]">
              {deleteTransaction && (
                <>Are you sure you want to delete this transaction record for {formatAmount(deleteTransaction.amount, deleteTransaction.currency)} from {deleteTransaction.user_email}? This action cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDeleteTransaction(null)} className="text-[#888] hover:text-white">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleteLoading} className="bg-red-600 hover:bg-red-700">
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
